/**
 * panels.tsx — stage strip, guidance (nudges + compliance + buying-signal
 * line), and the smart checklist (GUI v2 features 2 + 3, reqs A.3/A.5/A.6).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type {
  ChecklistItemState,
  ChecklistState,
  CriterionKey,
  Evidence,
  StageId,
  TranscriptLine,
} from "@/types";
import { ZONES, zoneIndex } from "@/live/content/stages";
import { STAGE_ZONE } from "@/types";
import { complianceAlert } from "@/live/content/compliance";
import type { ComplianceHit, PanelNudge } from "@/live/state";

export function PanelLabel({ children }: { children: ReactNode }) {
  return <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h2>;
}

/* ── Stage strip (req A.1: six zones derived from the active 12-stage) ────── */

export function StageStrip({ stageId }: { stageId: StageId }) {
  const activeIdx = zoneIndex(STAGE_ZONE[stageId]);
  return (
    <div className="flex items-center gap-1" aria-label="Call flow">
      {ZONES.map((zone, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <div key={zone.key} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <div
              className={[
                "h-1.5 w-full rounded-full",
                done ? "bg-emerald-500" : active ? "bg-primary" : "bg-muted",
              ].join(" ")}
            />
            <span
              className={[
                "max-w-full truncate text-[11px] leading-tight",
                active ? "font-semibold text-primary" : done ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground",
              ].join(" ")}
            >
              {zone.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Compliance alert (req G.3 — red, interrupts, shows the approved line) ── */

export function ComplianceBanner({ hit, onDismiss }: { hit: ComplianceHit; onDismiss: () => void }) {
  const def = complianceAlert(hit.alertId);
  return (
    <div role="alert" className="rounded-lg border-2 border-red-500 bg-red-50 p-3 dark:bg-red-950/40">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-bold text-red-700 dark:text-red-300">
          {def.kind}: {def.alert}
        </p>
        <button type="button" onClick={onDismiss} className="rounded-md px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 dark:text-red-300">
          Got it
        </button>
      </div>
      <p className="mt-1 text-sm text-red-800/90 dark:text-red-200/90">Say instead: “{def.approved}”</p>
    </div>
  );
}

/* ── Nudges (reqs 3.x + A.3 + A.6) ─────────────────────────────────────────── */

export interface NudgeActions {
  done(n: PanelNudge): void;
  dismiss(n: PanelNudge): void;
}

function WhyPopover({ nudge }: { nudge: PanelNudge }) {
  return (
    <div className="mt-2 rounded-md border bg-muted/50 p-2 text-xs text-muted-foreground">
      {nudge.evidence ? (
        <p>
          “{nudge.evidence.text}” <span className="opacity-70">— {nudge.evidence.speaker}</span>
        </p>
      ) : (
        <p>Suggested from the call so far.</p>
      )}
      {nudge.sourceRef && <p className="mt-1 opacity-80">Guide: {nudge.sourceRef}</p>}
    </div>
  );
}

function NudgeCard({ nudge, primary, actions }: { nudge: PanelNudge; primary: boolean; actions: NudgeActions }) {
  const [showWhy, setShowWhy] = useState(false);
  const urgent = nudge.severity === "urgent" || nudge.kind === "compliance";
  return (
    <div
      className={[
        "rounded-lg border p-3 transition",
        primary ? "live-nudge-pulse shadow-sm" : "opacity-70",
        urgent
          ? "border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/30"
          : "border-l-4 border-l-primary bg-card",
      ].join(" ")}
    >
      <p className={primary ? "text-base font-semibold leading-snug" : "text-sm font-medium leading-snug"}>
        {nudge.text}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={() => setShowWhy((v) => !v)} className="min-h-[32px] rounded-md border px-2.5 text-xs font-medium hover:bg-accent">
          WHY
        </button>
        <button type="button" onClick={() => actions.done(nudge)} className="min-h-[32px] rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white hover:opacity-90">
          DONE
        </button>
        <button type="button" onClick={() => actions.dismiss(nudge)} className="min-h-[32px] rounded-md border px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent">
          DISMISS
        </button>
      </div>
      {showWhy && <WhyPopover nudge={nudge} />}
    </div>
  );
}

/**
 * One primary + at most one secondary nudge (req A.3). The buying-signal
 * coaching line renders collapsed + dimmed until a buying signal lands, then
 * promotes to the primary slot.
 */
export function Guidance({
  nudges,
  buyingSignal,
  buyingSignalSeen,
  compliance,
  onDismissCompliance,
  actions,
}: {
  nudges: PanelNudge[];
  buyingSignal: boolean;
  /** True once the close panel has taken over (suppresses the promo line). */
  buyingSignalSeen: boolean;
  compliance: ComplianceHit | null;
  onDismissCompliance: () => void;
  actions: NudgeActions;
}) {
  const visible = nudges.slice(0, 2);
  return (
    <section aria-label="Guidance" className="space-y-2">
      {compliance && <ComplianceBanner hit={compliance} onDismiss={onDismissCompliance} />}
      {buyingSignal && !buyingSignalSeen && (
        <div className="rounded-lg border-l-4 border-l-emerald-500 bg-emerald-50 p-3 dark:bg-emerald-950/30">
          <p className="text-base font-semibold">Buying signal — offer two times, not “when works for you?”</p>
        </div>
      )}
      {visible.map((n, i) => (
        <NudgeCard key={n.id} nudge={n} primary={i === 0} actions={actions} />
      ))}
      {!buyingSignal && (
        <p className="truncate px-1 text-xs text-muted-foreground/60">Close coach: offer two times, not “when works?”</p>
      )}
      {visible.length === 0 && !compliance && (
        <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
          Prompts appear here as the conversation unfolds.
        </p>
      )}
    </section>
  );
}

/* ── Live transcript (rep / customer split, under the checklist) ──────────── */

export function TranscriptPanel({ lines }: { lines: TranscriptLine[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Pin to the latest line as the conversation grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);
  return (
    <section aria-label="Live transcript" className="space-y-1.5">
      <PanelLabel>Transcript</PanelLabel>
      <div ref={scrollRef} className="max-h-72 space-y-1 overflow-y-auto rounded-lg border bg-card p-2">
        {lines.length === 0 ? (
          <p className="text-xs text-muted-foreground">The conversation will appear here as it happens…</p>
        ) : (
          lines.map((l, i) => {
            const rep = l.speaker === "rep";
            const cust = l.speaker === "customer";
            return (
              <div key={i} className={rep ? "text-right" : "text-left"}>
                <span
                  className={[
                    "inline-block max-w-[88%] rounded-md px-2 py-1 text-xs leading-snug",
                    rep
                      ? "bg-primary/10 text-foreground"
                      : cust
                        ? "bg-muted text-foreground"
                        : "bg-muted/40 text-muted-foreground",
                  ].join(" ")}
                >
                  <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {rep ? "Rep" : cust ? "Cust" : "—"}
                  </span>
                  {l.text}
                </span>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

/* ── Smart checklist (feature 2; score secondary per req A.5) ─────────────── */

function EvidenceLine({ evidence }: { evidence: Evidence }) {
  return (
    <p className="mt-1 rounded-md bg-muted/50 p-1.5 text-xs text-muted-foreground">
      “{evidence.text}” <span className="opacity-70">— {evidence.speaker}</span>
    </p>
  );
}

function ChecklistRow({
  item,
  expanded,
  onToggle,
}: {
  item: ChecklistItemState;
  expanded: boolean;
  onToggle: (key: CriterionKey) => void;
}) {
  const [showEvidence, setShowEvidence] = useState(false);
  return (
    <li className={["rounded-md px-2 py-1.5", item.covered ? "bg-emerald-50 dark:bg-emerald-950/30" : ""].join(" ")}>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          aria-label={`Toggle ${item.label}`}
          onClick={() => onToggle(item.key)}
          className={[
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold",
            item.covered ? "border-emerald-500 bg-emerald-500 text-white" : "border-muted-foreground/40 text-transparent",
          ].join(" ")}
        >
          ✓
        </button>
        <span className={`flex-1 truncate text-sm ${item.covered ? "font-medium text-emerald-900 dark:text-emerald-100" : ""}`}>
          {item.label}
        </span>
        {item.covered && item.evidence && (
          <button type="button" onClick={() => setShowEvidence((v) => !v)} className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-accent">
            evidence
          </button>
        )}
        <span className="text-xs tabular-nums text-muted-foreground">{item.weight}</span>
      </div>
      {showEvidence && item.evidence && <EvidenceLine evidence={item.evidence} />}
      {/* Sub-checks visible while the item is open or expanded (req 2.3 / A.4) */}
      {item.subItems && (expanded || !item.covered) && (
        <div className="ml-8 mt-1 flex gap-2">
          {item.subItems.map((s) => (
            <span
              key={s.key}
              className={[
                "rounded-full px-2 py-0.5 text-[11px] font-medium",
                s.covered
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                  : "bg-muted text-muted-foreground",
              ].join(" ")}
            >
              {s.covered ? "✓ " : ""}
              {s.label}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

export function Checklist({
  checklist,
  onToggle,
  expanded = false,
}: {
  checklist: ChecklistState;
  onToggle: (key: CriterionKey) => void;
  expanded?: boolean;
}) {
  const total = checklist.reduce((sum, c) => sum + (c.covered ? c.weight : 0), 0);
  return (
    <section aria-label="Checklist" className="space-y-1.5">
      <div className="flex items-center justify-between">
        <PanelLabel>Checklist</PanelLabel>
        {/* Score is visually secondary during the call (req A.5) */}
        <span className="text-xs tabular-nums text-muted-foreground">{total}/100</span>
      </div>
      <ul className="space-y-1 rounded-lg border bg-card p-1.5">
        {checklist.map((c) => (
          <ChecklistRow key={c.key} item={c} expanded={expanded} onToggle={onToggle} />
        ))}
      </ul>
    </section>
  );
}
